import React, { useEffect, useMemo, useState } from 'react';
import { ArrowDownToLine, Check, Clock, Shuffle } from 'lucide-react';
import CardImage from '../../../ui/CardImage';
import CardOverlays from '../../../ui/CardOverlays';
import { cardImageProps } from '../utils/cardView';
import './MulliganOverlay.css';

const cardsLabel = (n) => (n === 1 ? '1 Karte' : `${n} Karten`);

/**
 * So lange bleiben die Knöpfe nach „Behalten" ohne Echo gesperrt. Lehnt der Server ab, kommt kein
 * Echo (nur ein Toast des Socket-Hooks); danach darf der Nutzer es erneut versuchen.
 */
export const KEEP_PENDING_MS = 5000;

/**
 * Mulligan-Phase (Spec 2026-10-21 §5.1): Vorschlag statt Zwang.
 *
 *   <MulliganOverlay hand={me.hand} mulliganCount={me.mulliganCount}
 *     recommendedBottomCount={state.cardsToBottomCount} isCommanderMode={state.isCommanderMode}
 *     kept={me.mulliganKept} waitingFor={['Anna']} onTakeMulligan={takeMulligan} onKeep={keepHand}
 *     previewBind={hover.bind} previewFlipFor={previewFlipFor} isMobile={isMobile} />
 *
 * - Jede Handkarte ist per Klick wählbar (beliebig viele, auch keine); gewählte Karten gehen beim
 *   Behalten unter die Bibliothek. `onKeep(instanceIds)` erhält sie in Hand-Reihenfolge.
 * - „Behalten" ist immer aktiv, auch bei einer Hand unter 7 Karten (fast leere Bibliothek); mit
 *   Auswahl heißt er „Behalten ({k} nach unten)".
 * - Der London-Hinweis steht nur bei `recommendedBottomCount > 0` und ist reine Information.
 * - Nach dem Behalten (`kept`, allein der Server-Zustand) zeigt das Overlay die behaltene Hand und
 *   „Warte auf: {Namen}" aus `waitingFor`; Knöpfe gibt es dann keine mehr. Der eigene Klick wartet
 *   nicht lokal: bleibt das Echo aus oder lehnt der Server ab, stehen die Knöpfe weiter da. Bis
 *   zum Echo sind sie gesperrt (ein zweites „Behalten" brächte nur die Ablehnung
 *   `MULLIGAN_ALREADY_KEPT`); die Sperre endet mit `kept`, einer neuen Hand oder nach
 *   `KEEP_PENDING_MS`.
 * - Kartengröße: sieben Karten nebeneinander füllen die Fläche, begrenzt durch Breite UND Höhe
 *   des Fensters (abzüglich Kopf, Hinweisen und Knöpfen) — am Handy quer die volle Höhe.
 * - `previewBind(card)`: Zeiger-Handler der schwebenden Vorschau je Karte (Rechner).
 * - Umdrehen hat zwei Arten, je nach `isMobile`:
 *   - Rechner (`isMobile` false): jede Karte zeigt gesteuert ihre echte Seite (`transformedFace`).
 *     `previewFlipFor(card)` liefert die Dreh-Funktion für die Karte, deren Großansicht steht,
 *     sonst `undefined`; nur diese Karte zeigt den Dreh-Knopf, er dreht die Großansicht.
 *   - Handy (`isMobile` true): die Übersicht ist selbst das Ansichts-Fenster (Spec
 *     2026-10-31-playtest-rueckseite-ansehen §9 P10). Touch-Geräte haben keine Hover-Vorschau,
 *     ohne eigenen Knopf bliebe die Rückseite einer Starthand-Karte unsichtbar. Jede doppelseitige
 *     Karte startet mit ihrer echten Seite und trägt einen Knopf, der nur lokal dreht;
 *     `previewFlipFor` wird nicht befragt.
 * - Ein Mulligan verwirft die Auswahl; Karten, die nicht mehr auf der Hand sind, zählen nie mit.
 * - Kein `useCloseOnBack`: die Phase lässt sich nicht schließen, nur durch Behalten beenden.
 */
export default function MulliganOverlay({
  hand = [],
  mulliganCount = 0,
  recommendedBottomCount = 0,
  isCommanderMode = false,
  kept = false,
  waitingFor = [],
  onTakeMulligan,
  onKeep,
  previewBind,
  previewFlipFor,
  isMobile = false,
}) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [sending, setSending] = useState(false);
  const isKept = kept;

  // Eine neue Hand (Mulligan, Zurücksetzen) oder der Server-Zustand beendet das Warten aufs Echo.
  // Der Schlüssel statt des Arrays: die Seite reicht bei jedem Rendern ein neues Hand-Array.
  const handKey = hand.map((card) => card.instanceId).join(',');
  useEffect(() => {
    setSending(false);
  }, [kept, handKey]);

  useEffect(() => {
    if (!sending) return undefined;
    const timer = setTimeout(() => setSending(false), KEEP_PENDING_MS);
    return () => clearTimeout(timer);
  }, [sending]);

  const selected = useMemo(
    () => hand.filter((card) => selectedIds.includes(card.instanceId)).map((card) => card.instanceId),
    [hand, selectedIds],
  );

  const toggle = (instanceId) => {
    setSelectedIds((prev) => (prev.includes(instanceId)
      ? prev.filter((id) => id !== instanceId)
      : [...prev, instanceId]));
  };

  const takeMulligan = () => {
    setSelectedIds([]);
    onTakeMulligan?.();
  };

  const keep = () => {
    setSending(true);
    onKeep?.(selected);
  };

  const title = mulliganCount > 0 ? `${mulliganCount}. Mulligan` : 'Starthand';

  return (
    <div className="mulligan-overlay" data-testid="mulligan-overlay">
      <section className="mulligan-overlay__panel" aria-label="Mulligan">
        <header className="mulligan-overlay__header">
          <h2 className="mulligan-overlay__title">{title}</h2>
          <span className="mulligan-overlay__count">{cardsLabel(hand.length)}</span>
        </header>

        {!isKept && recommendedBottomCount > 0 && (
          <p className="mulligan-overlay__hint">
            {`London-Mulligan: ${cardsLabel(recommendedBottomCount)} unter die Bibliothek empfohlen`}
          </p>
        )}
        {!isKept && isCommanderMode && mulliganCount === 0 && (
          <p className="mulligan-overlay__hint">Commander: Der erste Mulligan ist kostenlos.</p>
        )}
        {!isKept && (
          <p className="mulligan-overlay__help">Karten anklicken, die unter die Bibliothek sollen.</p>
        )}

        <ul className="mulligan-overlay__hand">
          {hand.map((card) => {
            const isSelected = !isKept && selected.includes(card.instanceId);
            const { face, ...imageProps } = cardImageProps(card);
            const sideProps = isMobile
              ? { defaultFace: face }
              : { face, onFlip: previewFlipFor?.(card) };
            return (
              <li
                key={card.instanceId}
                className="mulligan-overlay__card"
                data-selected={isSelected ? 'true' : undefined}
                {...(previewBind ? previewBind(card) : {})}
              >
                <CardImage
                  {...imageProps}
                  {...sideProps}
                  onOpen={isKept ? undefined : () => toggle(card.instanceId)}
                  openLabel={isSelected
                    ? `${imageProps.alt}: kommt unter die Bibliothek`
                    : `${imageProps.alt}: unter die Bibliothek legen`}
                >
                  {isSelected && (
                    <CardOverlays>
                      <CardOverlays.Corner position="top-left">
                        <CardOverlays.Badge passive>
                          <ArrowDownToLine size={12} aria-hidden /> Nach unten
                        </CardOverlays.Badge>
                      </CardOverlays.Corner>
                    </CardOverlays>
                  )}
                </CardImage>
              </li>
            );
          })}
        </ul>

        <footer className="mulligan-overlay__actions">
          {isKept ? (
            <p className="mulligan-overlay__waiting" role="status">
              <Clock size={16} aria-hidden />
              {waitingFor.length > 0 ? `Warte auf: ${waitingFor.join(', ')}` : 'Alle haben behalten.'}
            </p>
          ) : (
            <>
              <button type="button" className="btn-secondary" onClick={takeMulligan} disabled={sending}>
                <Shuffle size={16} aria-hidden />
                Mulligan
              </button>
              <button type="button" className="btn-primary" onClick={keep} disabled={sending}>
                <Check size={16} aria-hidden />
                {selected.length > 0 ? `Behalten (${selected.length} nach unten)` : 'Behalten'}
              </button>
            </>
          )}
        </footer>
      </section>
    </div>
  );
}
